import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useForm } from "react-hook-form";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { useNavigate } from "react-router-dom";
import { useAddAssetsLiabilitiesImages } from "app/hooks/assetsLiabilities/useAddAssetsLiabilitiesImages";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import toast from "react-hot-toast";
import DynamicSignature from "app/components/Individual/signature/DynamicSignature";
import { usePresignedUrls } from "app/hooks/assetsLiabilities/usePresignedUrls";
import { useS3Upload } from "app/hooks/assetsLiabilities/useS3Upload";
import { getPresignedUrlsPromise } from "app/utils/getPresignedUrlsPromise";
import Loader from "app/components/common/Loader";

interface FormData {
  yoursTerms: boolean;
  coBorrowerTerms: boolean;
}

const DeclarationSignaturesPartOne: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const sigCanvas = useRef<SignatureCanvas>(null);
  const sigSecoundCanvas = useRef<SignatureCanvas>(null);
  const { mutate: getPresignedUrls } = usePresignedUrls();
  const { mutate: uploadS3 } = useS3Upload();
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [coSignatureError, setCoSignatureError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { mutateAsync: uploadImages, isPending } =
    useAddAssetsLiabilitiesImages();

  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );

  // Function to clear the signature canvas
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };
  const clearOtherSignature = () => {
    if (sigSecoundCanvas.current) {
      sigSecoundCanvas.current.clear();
    }
  };

  // Function to save the signature as an image
  const saveSignature = () => {
    if (sigCanvas.current) {
      const signatureData = sigCanvas.current
        .getTrimmedCanvas()
        .toDataURL("image/png");
    }
  };

  const dataURLtoFile = (dataUrl: string, filename: string) => {
    let arr = dataUrl.split(","),
      mime = arr[0].match(/:(.*?);/)![1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm<FormData>();
  const yoursTerms = watch("yoursTerms");
  const coBorrowerTerms = watch("coBorrowerTerms");
  const isDisabled = !yoursTerms || !coBorrowerTerms;

  const onSubmit = async (data: FormData) => {
    if (!savedIds?.applicantID && !savedIds?.secondApplicantID) {
      toast.error("Applicant ID is missing.");
      return;
    }

    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      setSignatureError("Digital signature is required.");
      return;
    } else {
      setSignatureError(null);
    }

    if (!sigSecoundCanvas.current || sigSecoundCanvas.current.isEmpty()) {
      setCoSignatureError("Digital signature is required.");
      return;
    } else {
      setCoSignatureError(null);
    }

    const signatureData = sigCanvas?.current
      .getTrimmedCanvas()
      .toDataURL("image/png");

    const secoundSignatureData = sigCanvas?.current
      .getTrimmedCanvas()
      .toDataURL("image/png");

    const signatureFile = dataURLtoFile(signatureData, "signature.png");

    const secoundSignatureFile = dataURLtoFile(
      secoundSignatureData,
      "signature.png"
    );

    try {
      setIsLoading(true);
      const presignedResponse = await getPresignedUrlsPromise({
        url: `/document/${savedIds.applicantID}/presigned-urls`,
        data: { documentTypes: ["signature"] },
      });

      const secondPresignedResponse = await getPresignedUrlsPromise({
        url: `/document/${savedIds.secondApplicantID}/presigned-urls`,
        data: { documentTypes: ["signature"] },
      });
      const firstKey = presignedResponse?.signature?.key;
      const firstUrl = presignedResponse?.signature?.url;

      const secondKey = secondPresignedResponse?.signature?.key;
      const secondUrl = secondPresignedResponse?.signature?.url;

      if (!firstKey || !firstUrl || !secondKey || !secondUrl) {
        throw new Error("Missing presigned URL or key.");
      }
      await Promise.all([
        uploadS3({ url: firstUrl, data: signatureFile }),
        uploadS3({ url: secondUrl, data: secoundSignatureFile }),
      ]);

      await Promise.all([
        uploadImages({
          url: `/document/${savedIds.applicantID}/signature`,
          data: {
            signature: {
              key: firstKey,
              fileName: signatureFile.name,
              mimeType: signatureFile.type,
            },
          },
        }),
        uploadImages({
          url: `/document/${savedIds.secondApplicantID}/signature`,
          data: {
            signature: {
              key: secondKey,
              fileName: secoundSignatureFile.name,
              mimeType: secoundSignatureFile.type,
            },
          },
        }),
      ]);

      sigCanvas.current?.clear();
      sigSecoundCanvas.current?.clear();
      navigate("/success");
    } catch (error: any) {
      setIsLoading(false);
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const handlePrevious = () => {
    navigate(`/joint-assets-liabilities`);
  };

  return (
    <>
      {isLoading && (
        <Loader text="Please wait while your documents are being uploaded..." />
      )}
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Declaration & Consent" />
      </div>
      <div className="mx-auto w-internalWidth">
        <div className="flex mb-5 flex-col xl:flex-row gap-3 sm:gap-4 ">
          <div className="flex-1">
            <InfoTitle title="Your Details" />
            <DynamicSignature
              sigCanvas={sigCanvas}
              clearSignature={clearSignature}
              signatureError={signatureError}
              setSignatureError={setSignatureError}
              control={control}
              id="joint-sigCanvas"
              terms="yoursTerms"
            />
          </div>
          <div className="flex-1">
            <InfoTitle title="Co-borrower" />
            <DynamicSignature
              sigCanvas={sigSecoundCanvas}
              clearSignature={clearOtherSignature}
              signatureError={coSignatureError}
              setSignatureError={setCoSignatureError}
              control={control}
              id="coBorrowerTerms"
              terms="coBorrowerTerms"
            />
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton
              type="submit"
              loading={isPending}
              disabled={isDisabled}
            >
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default DeclarationSignaturesPartOne;
